import React from 'react';
import {
    Box,
    Typography,
    Dialog,
    DialogContent,
    CircularProgress,
    ButtonProps, Button
} from '@mui/material';

import palette from "../../theme/palette";

// Simple LoadingButton with Popup
interface LoadingButtonWithPopupProps extends Omit<ButtonProps, 'onClick'> {
    loading: boolean;
    loadingText?: string;
    onClick: () => void | Promise<void>;
    children: React.ReactNode;
}

export const LoadingButtonWithPopup: React.FC<LoadingButtonWithPopupProps> = ({
                                                                                  loading,
                                                                                  loadingText = 'Processing...',
                                                                                  onClick,
                                                                                  children,
                                                                                  disabled,
                                                                                  variant = 'contained',
                                                                                  ...buttonProps
                                                                              }) => {
    const btnColor = palette.darkBlue;

    return (
        <>
            <Button
                variant="alternate"
                onClick={onClick}
                disabled={disabled || loading}
                fullWidth
                sx={{ py: 1.5, px : 2 }}
            >
                {children}
            </Button>

            <Dialog
                open={loading}
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                        borderRadius: 3,
                        minWidth: 350,
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    }
                }}
            >
                <DialogContent sx={{ p: 5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3
                        }}
                    >
                        <CircularProgress
                            size={70}
                            thickness={4}
                            sx={{
                                color: btnColor,
                                filter: 'drop-shadow(0 4px 8px rgba(25, 118, 210, 0.3))'
                            }}
                        />
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography
                                variant="h6"
                                color="text.primary"
                                fontWeight={600}
                                sx={{ mb: 1 }}
                            >
                                {loadingText}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ maxWidth: 320 }}
                            >
                                Please wait while we process your request. This may take a few moments...
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            {[0, 1, 2].map((i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: btnColor,
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                        animationDelay: `${i * 0.2}s`,
                                        '@keyframes pulse': {
                                            '0%, 100%': {
                                                opacity: 0.3,
                                                transform: 'scale(0.8)',
                                            },
                                            '50%': {
                                                opacity: 1,
                                                transform: 'scale(1.2)',
                                            },
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};